/**
 * Máy chủ giả cho hai màn cấu hình của eKYC: Quy trình định danh và Ứng dụng & khoá API.
 *
 * Đóng vai backend `modules/ekyc` (ADR-0085) khi chưa đăng nhập Keycloak thật hoặc khi URL có `?mock=`. Dữ liệu dựng
 * quanh các nhánh cần soát bằng mắt:
 *
 * - Ứng dụng `MAC_DINH` **chưa cấp bí mật webhook lần nào** (`phienBanBiMat: 0`) — màn phải mời "Cấp bí mật".
 * - `DOI_TAC_A` có một lượt giao webhook `FAILED` hết lượt thử — nút "Gửi lại" phải hiện.
 * - `DOI_TAC_CU` đã tắt.
 * - Quy trình `NHANH` tắt bước duyệt tay — phải thấy cảnh báo, nhưng vẫn lưu được.
 *
 * Luật kiểm ở đây là **bản rút gọn** của `modules/ekyc/backend/src/quy-trinh/chinh-sach.ts`, đủ để màn có lỗi và
 * cảnh báo mà vẽ. Nguồn thật là máy chủ: tầng này bị xoá khi API thật về. **Không quyết màu hay nhãn ở đây.**
 */
import type { ApiError } from '../lib/errors';
import type {
  BuocDanhMuc,
  BuocHieuLuc,
  ChinhSach,
  DanhMucQuyTrinh,
  DanhSachUngDung,
  LuotGiaoWebhook,
  NhomBuoc,
  QuyTrinh,
  QuyTrinhSua,
  QuyTrinhTao,
  SuDungUngDung,
  UngDung,
  UngDungCoBiMat,
  UngDungSua,
  UngDungTao,
  VanDeCauHinh,
  XemTruocQuyTrinh,
  YeuToRuiRo,
} from '../modules/ekyc/types';

const KHA_DUNG = new Set(['dong_y', 'mat_truoc', 'mat_sau', 'chan_dung', 'duyet_tay']);

function b(ma: string, nhom: NhomBuoc, macDinh: boolean, them: Partial<BuocDanhMuc> = {}): BuocDanhMuc {
  return { ma, nhom, macDinh, khoa: false, khaDung: KHA_DUNG.has(ma), ...them };
}

const DANH_MUC: BuocDanhMuc[] = [
  b('dong_y', 'thu-thap', true, { khoa: true }),
  b('mat_truoc', 'thu-thap', true, { khoa: true }),
  b('mat_sau', 'thu-thap', true),
  b('chan_dung', 'thu-thap', true, { tuBatKhi: ['thuc_the_song', 'doi_chieu_mat'] }),
  b('nfc', 'thu-thap', false),
  b('chat_luong_anh', 'giay-to', true, { khoa: true }),
  b('boc_tach', 'giay-to', true),
  b('mrz_logic', 'giay-to', true, { can: ['boc_tach'] }),
  b('khach_xac_nhan', 'giay-to', true, { can: ['boc_tach'] }),
  b('gia_mao', 'giay-to', true),
  b('thuc_the_song', 'khuon-mat', true, { can: ['chan_dung'] }),
  b('doi_chieu_mat', 'khuon-mat', true, { can: ['chan_dung', 'mat_truoc'] }),
  b('pad', 'khuon-mat', false, { can: ['chan_dung'] }),
  b('kiem_trung', 'doi-soat', true, { can: ['boc_tach'] }),
  b('doi_soat_co_quan', 'doi-soat', false, { can: ['boc_tach'] }),
  b('kiem_thiet_bi', 'doi-soat', false),
  b('cham_diem', 'quyet-dinh', true, { khoa: true }),
  b('duyet_tay', 'quyet-dinh', true),
];

const YEU_TO: YeuToRuiRo[] = ['document', 'ocr', 'fraud', 'liveness', 'pad', 'face', 'device', 'authority', 'behavior'];

function chinhSachMacDinh(): ChinhSach {
  return {
    weights: { document: 0.1, ocr: 0.08, fraud: 0.17, liveness: 0.15, pad: 0.15, face: 0.15, device: 0.08, authority: 0.08, behavior: 0.04 },
    thresholds: { pass: 0.3, reject: 0.7 },
    faceMatchThreshold: 0.5,
    livenessMode: 'PASSIVE',
    challengeActions: ['TURN_LEFT', 'TURN_RIGHT', 'LOOK_UP', 'LOOK_DOWN', 'MOVE_CLOSER'],
    challengeCount: 2,
    challengeRandomOrder: true,
    documentFields: ['idNumber', 'fullName', 'dob', 'gender', 'issueDate', 'expiryDate', 'residence'],
    ocrSource: '',
    onAuthorityUnavailable: 'MANUAL_REVIEW',
    onLivenessRetryExceeded: 'MANUAL_REVIEW',
    sendWebhook: false,
    maxDocumentRetry: 3,
    maxLivenessRetry: 3,
    minAge: null,
  };
}

function hieuLuc(delta: Record<string, boolean>): BuocHieuLuc[] {
  const yeuCau = new Map(DANH_MUC.map((x) => [x.ma, x.khoa ? true : delta[x.ma] ?? x.macDinh]));
  const bat = new Map<string, boolean>();
  const tuBat = new Map<string, boolean>();
  for (let vong = 0; vong < 3; vong++) {
    for (const x of DANH_MUC) {
      const tu = (x.tuBatKhi ?? []).some((m) => bat.get(m) === true);
      const du = (x.can ?? []).every((m) => bat.get(m) ?? yeuCau.get(m) === true);
      bat.set(x.ma, (yeuCau.get(x.ma)! || tu) && x.khaDung && du);
      tuBat.set(x.ma, tu && !yeuCau.get(x.ma)!);
    }
  }
  return DANH_MUC.map((x) => {
    const yc = yeuCau.get(x.ma)!;
    const on = bat.get(x.ma)!;
    const lyDoTat = on ? null : !(yc || tuBat.get(x.ma)) ? 'nguoi_dung_tat' : !x.khaDung ? 'chua_kha_dung' : 'thieu_phu_thuoc';
    return { ma: x.ma, nhom: x.nhom, bat: on, yeuCau: yc, khoa: x.khoa, khaDung: x.khaDung, tuBat: tuBat.get(x.ma)!, lyDoTat };
  });
}

function toiGian(delta: Record<string, boolean>): Record<string, boolean> {
  const ra: Record<string, boolean> = {};
  for (const x of DANH_MUC) if (typeof delta[x.ma] === 'boolean' && !x.khoa && delta[x.ma] !== x.macDinh) ra[x.ma] = delta[x.ma]!;
  return ra;
}

/** Bản rút gọn của `kiemQuyTrinh` ở máy chủ — chỉ các luật mà màn cần vẽ được. */
function kiem(buoc: Record<string, boolean>, cs: ChinhSach): VanDeCauHinh[] {
  const ds: VanDeCauHinh[] = [];
  const loi = (truong: string, ma: string, thongDiep: string) => ds.push({ muc: 'loi', truong, ma, thongDiep });
  for (const [ma, v] of Object.entries(buoc)) {
    const x = DANH_MUC.find((d) => d.ma === ma);
    if (!x) loi(`#/buoc/${ma}`, 'BUOC_LA', `Không có bước "${ma}".`);
    else if (x.khoa && !v) loi(`#/buoc/${ma}`, 'BUOC_KHOA', 'Bước này luôn bật, không tắt được.');
    else if (!x.khaDung && v) loi(`#/buoc/${ma}`, 'CHUA_KHA_DUNG', 'Bước này chưa khả dụng ở bản này, chưa bật được.');
  }
  const tong = YEU_TO.reduce((s, k) => s + (cs.weights[k] ?? 0), 0);
  if (Math.abs(tong - 1) > 0.001) loi('#/chinhSach/weights', 'TONG_TRONG_SO', `Tổng trọng số phải bằng 1 (hiện ${tong.toFixed(3)}).`);
  if (cs.thresholds.pass >= cs.thresholds.reject) {
    loi('#/chinhSach/thresholds/reject', 'THU_TU_NGUONG', 'Ngưỡng từ chối phải lớn hơn ngưỡng đạt.');
  }
  if (cs.faceMatchThreshold < 0.5 || cs.faceMatchThreshold > 0.95) {
    loi('#/chinhSach/faceMatchThreshold', 'NGOAI_KHOANG', 'Phải là số từ 0.5 đến 0.95.');
  }
  if (cs.challengeCount > Math.max(cs.challengeActions.length, 1)) {
    loi('#/chinhSach/challengeCount', 'NGOAI_KHOANG', `Phải là số nguyên từ 1 đến ${cs.challengeActions.length}.`);
  }
  if (buoc.duyet_tay === false) {
    ds.push({ muc: 'canh_bao', truong: '#/buoc/duyet_tay', ma: 'KHONG_DUYET_TAY',
      thongDiep: 'Tắt duyệt tay: hồ sơ rơi vào vùng cần người xem sẽ bị từ chối thẳng.' });
  }
  return ds;
}

function nemNeuLoi(ds: VanDeCauHinh[]): void {
  const loi = ds.filter((v) => v.muc === 'loi');
  if (loi.length) {
    throw { code: 'INVALID_ARGUMENT', status: 400, message: 'Quy trình chưa hợp lệ, xem lỗi từng trường.',
      details: loi.map((v) => ({ pointer: v.truong, detail: v.thongDiep })) } satisfies ApiError;
  }
}

const khongThay = (loai: string): ApiError => ({ code: 'NOT_FOUND', status: 404, message: `Không tìm thấy ${loai}.` });
const bayGio = () => new Date().toISOString();
const truocDo = (ngay: number) => new Date(Date.now() - ngay * 86_400_000).toISOString();
let soMoi = 0;
const idMoi = (tien: string) => `${tien}-moi-${++soMoi}`;

// ----- Dữ liệu ---------------------------------------------------------------------------------------------------

interface QuyTrinhLuu {
  id: string; ungDungId: string; ma: string; ten: string; moTa: string; macDinh: boolean; dangBat: boolean;
  phienBan: number; buoc: Record<string, boolean>; chinhSach: ChinhSach; createdAt: string; updatedAt: string;
}

const UNG_DUNG: UngDung[] = [
  {
    id: 'ud-001', ma: 'MAC_DINH', ten: 'Ứng dụng mặc định', webhookUrl: '', dangBat: true, phienBanBiMat: 0,
    soQuyTrinh: 0, quyTrinhMacDinh: null, phien30Ngay: 41, phienCuoiLuc: truocDo(0.1), webhookThatBai: 0,
    createdAt: truocDo(40), updatedAt: truocDo(40),
  },
  {
    id: 'ud-002', ma: 'APP_VI_DIEN_TU', ten: 'App ví điện tử', webhookUrl: 'https://api.doi-tac.example.vn/ekyc/webhook',
    dangBat: true, phienBanBiMat: 3, soQuyTrinh: 0, quyTrinhMacDinh: null, phien30Ngay: 1284, phienCuoiLuc: truocDo(0.01),
    webhookThatBai: 1, createdAt: truocDo(30), updatedAt: truocDo(2),
  },
  {
    id: 'ud-003', ma: 'DOI_TAC_CU', ten: 'Đối tác cũ (đã ngừng)', webhookUrl: 'https://cu.example.vn/hook', dangBat: false,
    phienBanBiMat: 1, soQuyTrinh: 0, quyTrinhMacDinh: null, phien30Ngay: 0, phienCuoiLuc: truocDo(90),
    webhookThatBai: 0, createdAt: truocDo(200), updatedAt: truocDo(60),
  },
];

const QUY_TRINH: QuyTrinhLuu[] = [
  { id: 'qt-001', ungDungId: 'ud-001', ma: 'CHUAN', ten: 'Quy trình chuẩn', moTa: 'Đủ mọi bước khả dụng của hệ thống.',
    macDinh: true, dangBat: true, phienBan: 1, buoc: {}, chinhSach: chinhSachMacDinh(), createdAt: truocDo(40), updatedAt: truocDo(40) },
  { id: 'qt-002', ungDungId: 'ud-002', ma: 'CHUAN', ten: 'Quy trình chuẩn', moTa: 'Đủ mọi bước khả dụng của hệ thống.',
    macDinh: true, dangBat: true, phienBan: 4, buoc: {}, chinhSach: { ...chinhSachMacDinh(), sendWebhook: true },
    createdAt: truocDo(30), updatedAt: truocDo(3) },
  { id: 'qt-003', ungDungId: 'ud-002', ma: 'NHANH', ten: 'Nhanh cho khách quen', moTa: 'Bỏ duyệt tay, chỉ dùng cho khoản nhỏ.',
    macDinh: false, dangBat: true, phienBan: 2, buoc: { duyet_tay: false }, chinhSach: chinhSachMacDinh(),
    createdAt: truocDo(10), updatedAt: truocDo(5) },
];

const LUOT_GIAO: LuotGiaoWebhook[] = [
  {
    id: 'wh-001', phienId: 'ek-001', referenceId: 'KH-2026-0091', ungDungId: 'ud-002', suKien: 'session.manual_review',
    url: 'https://api.doi-tac.example.vn/ekyc/webhook', trangThai: 'FAILED', soLanThu: 9, guiTiepLuc: null,
    lichSu: [
      { luc: truocDo(1.2), lan: 1, status: 503, loi: '', ms: 812 },
      { luc: truocDo(1.19), lan: 2, status: null, loi: 'Quá 10 giây không phản hồi.', ms: 10_000 },
      { luc: truocDo(0.2), lan: 9, status: 503, loi: '', ms: 640 },
    ],
    createdAt: truocDo(1.2), updatedAt: truocDo(0.2),
  },
  {
    id: 'wh-002', phienId: 'ek-002', referenceId: 'KH-2026-0090', ungDungId: 'ud-002', suKien: 'session.verified',
    url: 'https://api.doi-tac.example.vn/ekyc/webhook', trangThai: 'SENT', soLanThu: 1, guiTiepLuc: null,
    lichSu: [{ luc: truocDo(0.5), lan: 1, status: 200, loi: '', ms: 143 }],
    createdAt: truocDo(0.5), updatedAt: truocDo(0.5),
  },
  {
    id: 'wh-003', phienId: 'ek-005', referenceId: 'KH-2026-0089', ungDungId: 'ud-002', suKien: 'session.cancelled',
    url: 'https://api.doi-tac.example.vn/ekyc/webhook', trangThai: 'PENDING', soLanThu: 2, guiTiepLuc: new Date(Date.now() + 25_000).toISOString(),
    lichSu: [{ luc: truocDo(0.001), lan: 1, status: 500, loi: '', ms: 220 }, { luc: truocDo(0.0005), lan: 2, status: 500, loi: '', ms: 198 }],
    createdAt: truocDo(0.001), updatedAt: truocDo(0.0005),
  },
];

function raQuyTrinh(q: QuyTrinhLuu): QuyTrinh {
  return { id: q.id, ungDungId: q.ungDungId, ma: q.ma, ten: q.ten, moTa: q.moTa, macDinh: q.macDinh, dangBat: q.dangBat,
    phienBan: q.phienBan, steps: q.buoc, effectiveSteps: hieuLuc(q.buoc), chinhSach: q.chinhSach,
    vanDe: kiem(q.buoc, q.chinhSach), createdAt: q.createdAt, updatedAt: q.updatedAt };
}

function raUngDung(u: UngDung): UngDung {
  const cua = QUY_TRINH.filter((q) => q.ungDungId === u.id);
  const md = cua.find((q) => q.macDinh);
  return { ...u, soQuyTrinh: cua.length, quyTrinhMacDinh: md ? { id: md.id, ten: md.ten } : null };
}

function layUngDung(id: string): UngDung {
  const u = UNG_DUNG.find((x) => x.id === id);
  if (!u) throw khongThay('ứng dụng');
  return u;
}

function layQuyTrinh(id: string): QuyTrinhLuu {
  const q = QUY_TRINH.find((x) => x.id === id);
  if (!q) throw khongThay('quy trình định danh');
  return q;
}

/** Bí mật giả: đủ dạng để màn hiện và chép, không ký được gì. */
function biMatGia(): string {
  const chu = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
  let s = 'ekyc_wh_';
  for (let i = 0; i < 43; i++) s += chu[Math.floor(Math.random() * chu.length)];
  return s;
}

export const mayChuCauHinhEkyc = {
  danhMuc(): DanhMucQuyTrinh {
    return {
      nhom: ['thu-thap', 'giay-to', 'khuon-mat', 'doi-soat', 'quyet-dinh'],
      buoc: DANH_MUC,
      chinhSachMacDinh: chinhSachMacDinh(),
      luaChon: {
        yeuTo: YEU_TO,
        cheDoLiveness: ['PASSIVE', 'ACTIVE', 'HYBRID'],
        dongTac: ['TURN_LEFT', 'TURN_RIGHT', 'LOOK_UP', 'LOOK_DOWN', 'MOVE_CLOSER'],
        nguonOcr: ['', 'service', 'gemini', 'openai', 'local'],
        khiThieu: ['MANUAL_REVIEW', 'REJECT'],
        truongGiayTo: ['idNumber', 'oldIdNumber', 'fullName', 'dob', 'gender', 'issueDate', 'expiryDate', 'issuePlace',
          'residence', 'nationality', 'mrz'],
      },
    };
  },

  dsQuyTrinh(ungDungId: string): { items: QuyTrinh[] } {
    layUngDung(ungDungId);
    const ds = QUY_TRINH.filter((q) => q.ungDungId === ungDungId)
      .sort((a, c) => Number(c.macDinh) - Number(a.macDinh) || a.ten.localeCompare(c.ten, 'vi'));
    return { items: ds.map(raQuyTrinh) };
  },

  xemTruoc(buoc: Record<string, boolean>, chinhSach: ChinhSach): XemTruocQuyTrinh {
    return { steps: toiGian(buoc), effectiveSteps: hieuLuc(buoc), vanDe: kiem(buoc, chinhSach) };
  },

  taoQuyTrinh(du: QuyTrinhTao): QuyTrinh {
    layUngDung(du.ungDungId);
    nemNeuLoi(kiem(du.buoc, du.chinhSach));
    if (QUY_TRINH.some((q) => q.ungDungId === du.ungDungId && q.ma === du.ma)) {
      throw { code: 'EKYC_WORKFLOW_CODE_EXISTS', status: 409, message: 'Ứng dụng này đã có quy trình mang mã này.',
        details: [{ pointer: '#/ma', detail: 'Mã đã được dùng. Đổi tên quy trình để sinh mã khác.' }] } satisfies ApiError;
    }
    const q: QuyTrinhLuu = {
      id: idMoi('qt'), ungDungId: du.ungDungId, ma: du.ma, ten: du.ten, moTa: du.moTa,
      macDinh: !QUY_TRINH.some((x) => x.ungDungId === du.ungDungId), dangBat: true, phienBan: 1,
      buoc: toiGian(du.buoc), chinhSach: du.chinhSach, createdAt: bayGio(), updatedAt: bayGio(),
    };
    QUY_TRINH.push(q);
    return raQuyTrinh(q);
  },

  suaQuyTrinh(id: string, du: QuyTrinhSua): QuyTrinh {
    const q = layQuyTrinh(id);
    if (q.phienBan !== du.phienBan) {
      throw { code: 'EKYC_WORKFLOW_STALE', status: 409,
        message: 'Quy trình vừa được người khác lưu. Tải lại bản mới nhất rồi sửa tiếp.' } satisfies ApiError;
    }
    const buoc = du.buoc ?? q.buoc;
    const cs = du.chinhSach ?? q.chinhSach;
    if (du.buoc || du.chinhSach) nemNeuLoi(kiem(buoc, cs));
    Object.assign(q, { ten: du.ten ?? q.ten, moTa: du.moTa ?? q.moTa, buoc: toiGian(buoc), chinhSach: cs,
      dangBat: du.dangBat ?? q.dangBat, phienBan: q.phienBan + 1, updatedAt: bayGio() });
    return raQuyTrinh(q);
  },

  datMacDinh(id: string): QuyTrinh {
    const q = layQuyTrinh(id);
    for (const x of QUY_TRINH) if (x.ungDungId === q.ungDungId) x.macDinh = x.id === id;
    return raQuyTrinh(q);
  },

  xoaQuyTrinh(id: string): void {
    const q = layQuyTrinh(id);
    if (q.macDinh) {
      throw { code: 'EKYC_WORKFLOW_IS_DEFAULT', status: 409,
        message: 'Không xoá được quy trình mặc định. Đặt quy trình khác làm mặc định trước.' } satisfies ApiError;
    }
    QUY_TRINH.splice(QUY_TRINH.indexOf(q), 1);
  },

  dsUngDung(): DanhSachUngDung {
    return { items: UNG_DUNG.map(raUngDung), coKhoaGoc: true };
  },

  suDung(id: string): SuDungUngDung {
    const u = layUngDung(id);
    const n = u.phien30Ngay;
    const theoNgay = Array.from({ length: 14 }, (_, i) => {
      const d = new Date(Date.now() - (13 - i) * 86_400_000);
      return { ngay: d.toISOString().slice(0, 10), soPhien: n === 0 ? 0 : Math.round((n / 30) * (0.6 + ((i * 37) % 9) / 10)) };
    });
    const daDuyet = Math.round(n * 0.71);
    const tuChoi = Math.round(n * 0.08);
    const choDuyet = Math.round(n * 0.06);
    const roi = Math.round(n * 0.12);
    return {
      tong: n,
      ketCuc: { daDuyet, tuChoi, choDuyet, roi, loi: 0, daXoa: 0, dangLam: Math.max(0, n - daDuyet - tuChoi - choDuyet - roi) },
      theoNgay,
      webhook: id === 'ud-002' ? { choGui: 1, daGui: 1190, thatBai: 1 } : { choGui: 0, daGui: 0, thatBai: 0 },
    };
  },

  taoUngDung(du: UngDungTao): UngDungCoBiMat {
    if (UNG_DUNG.some((u) => u.ma === du.ma)) {
      throw { code: 'EKYC_APP_CODE_EXISTS', status: 409, message: 'Đã có ứng dụng mang mã này.',
        details: [{ pointer: '#/ma', detail: 'Mã đã được dùng. Đổi tên ứng dụng để sinh mã khác.' }] } satisfies ApiError;
    }
    const u: UngDung = { id: idMoi('ud'), ma: du.ma, ten: du.ten, webhookUrl: du.webhookUrl, dangBat: true, phienBanBiMat: 1,
      soQuyTrinh: 1, quyTrinhMacDinh: null, phien30Ngay: 0, phienCuoiLuc: null, webhookThatBai: 0,
      createdAt: bayGio(), updatedAt: bayGio() };
    UNG_DUNG.push(u);
    QUY_TRINH.push({ id: idMoi('qt'), ungDungId: u.id, ma: 'CHUAN', ten: 'Quy trình chuẩn',
      moTa: 'Đủ mọi bước khả dụng của hệ thống.', macDinh: true, dangBat: true, phienBan: 1, buoc: {},
      chinhSach: chinhSachMacDinh(), createdAt: bayGio(), updatedAt: bayGio() });
    return { ...raUngDung(u), webhookSecret: biMatGia() };
  },

  suaUngDung(id: string, du: UngDungSua): UngDung {
    const u = layUngDung(id);
    Object.assign(u, { ...du, updatedAt: bayGio() });
    return raUngDung(u);
  },

  xoayBiMat(id: string): UngDungCoBiMat {
    const u = layUngDung(id);
    u.phienBanBiMat += 1;
    return { ...raUngDung(u), webhookSecret: biMatGia() };
  },

  batTat(id: string, bat: boolean): UngDung {
    const u = layUngDung(id);
    u.dangBat = bat;
    return raUngDung(u);
  },

  luotGiao(ungDungId: string): { items: LuotGiaoWebhook[] } {
    layUngDung(ungDungId);
    return { items: LUOT_GIAO.filter((w) => w.ungDungId === ungDungId) };
  },

  thuLai(id: string): LuotGiaoWebhook {
    const w = LUOT_GIAO.find((x) => x.id === id);
    if (!w) throw khongThay('lượt gửi webhook');
    if (w.trangThai !== 'FAILED') {
      throw { code: 'EKYC_DELIVERY_NOT_FAILED', status: 409,
        message: 'Chỉ gửi lại được lượt đã thất bại hết số lần thử.' } satisfies ApiError;
    }
    // Máy chủ giả "gửi" ngay và thành công, để màn soát được dòng đổi trạng thái.
    w.soLanThu += 1;
    w.trangThai = 'SENT';
    w.lichSu = [...w.lichSu, { luc: bayGio(), lan: w.soLanThu, status: 200, loi: '', ms: 180 }];
    return w;
  },
};
